import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Info, Plus, Minus, History, MessageSquareQuote, Timer } from 'lucide-react';
import { ClientShell } from '../components/ClientShell';
import { RestTimer } from '../components/RestTimer';
import { ExerciseDetailDialog } from '../components/ExerciseDetailDialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { loadSaved, saveState, clearSaved } from '../lib/workoutPlayer';
import { WARMUP_REST_SECONDS, warmupsOf, warmupText } from '../lib/warmupSets';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };
const kg = (w) => (w == null || w === '' ? '' : String(w).replace('.', ','));
// Row label: warm-up rows (always first) numbered on their own, working sets from 1.
const rowLabels = (sets) => {
  const warm = sets.filter((s) => s.warmup).length;
  return sets.map((s, i) => (s.warmup ? `Rozgrz. ${i + 1}` : `Seria ${i - warm + 1}`));
};
const fmtLast = (sets) => sets.map((s) => `${s.weight != null ? `${kg(s.weight)} kg × ` : ''}${s.reps || '?'}`).join(', ');
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('pl-PL', { day: 'numeric', month: 'numeric' });

// One exercise of the day: plan target, trainer's note, last time's result
// and editable set rows (kg + reps) with the client's own note.
const ExerciseCard = ({ ex, entry, last, onChange, onInfo, restSeconds, onRest }) => {
  const setRow = (i, patch) => onChange({ ...entry, sets: entry.sets.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const target = [ex.sets && `${ex.sets} ×`, ex.reps, ex.weight_kg != null && `@ ${kg(ex.weight_kg)} kg`].filter(Boolean).join(' ');
  const warmups = warmupsOf(ex);
  const labels = rowLabels(entry.sets);
  const warmCount = entry.sets.filter((s) => s.warmup).length;
  const extra = [ex.tempo && `tempo ${ex.tempo}`, ex.rir != null && `RIR ${ex.rir}`, ex.rest_seconds && `przerwa ${ex.rest_seconds} s`].filter(Boolean).join(' · ');
  return (
    <section className="glass rounded-[26px] p-5 sm:p-6 flex flex-col gap-3.5" data-testid="journal-exercise">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {(ex.superset_group || ex.circuit_group) && (
            <p className="text-xs font-semibold text-[#C9A84C] mb-0.5">{ex.superset_group ? `Super-seria ${ex.superset_group}` : ex.circuit_group}</p>
          )}
          <h3 className="text-lg font-extrabold tracking-tight">{ex.exercise_name}</h3>
          <p className="text-sm text-[var(--text-muted)]">Plan: {target || '—'}{extra && ` · ${extra}`}</p>
          {warmups.length > 0 && (
            <p className="text-sm text-[#C9A84C]">Najpierw rozgrzewkowe: {warmups.map(warmupText).join(', ')}</p>
          )}
        </div>
        {onInfo && (
          <button type="button" onClick={onInfo} className="chip shrink-0"><Info className="w-3.5 h-3.5" /> Jak?</button>
        )}
      </div>
      {ex.notes && (
        <p className="text-sm leading-relaxed"><span className="font-bold text-[#C9A84C]">Trener: </span>{ex.notes}</p>
      )}
      {last && (
        <div className="glass-soft rounded-2xl px-3.5 py-2.5 text-xs text-[var(--text-muted)] flex flex-col gap-1">
          <span className="flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Ostatnio ({shortDate(last.date)}): <b className="text-[var(--text-primary)] font-semibold">{fmtLast(last.sets) || '—'}</b></span>
          {last.notes && <span>Twoja uwaga: „{last.notes}”</span>}
          {last.trainer_comment && <span className="text-[#C9A84C]">Trener: „{last.trainer_comment}”</span>}
        </div>
      )}
      <div className="grid grid-cols-[62px_1fr_1fr_44px] gap-x-2 gap-y-2 items-center">
        <span />
        <span className="label-text text-[var(--text-muted)] text-[0.55rem] text-center">KG</span>
        <span className="label-text text-[var(--text-muted)] text-[0.55rem] text-center">POWT.</span>
        <span className="text-[10px] text-[var(--text-muted)] text-center">{restSeconds} s</span>
        {entry.sets.map((s, i) => (
          <React.Fragment key={i}>
            <span className={`text-xs whitespace-nowrap ${s.warmup ? 'text-[#C9A84C]' : 'text-[var(--text-muted)]'}`}>{labels[i]}</span>
            <Input inputMode="decimal" value={s.weight}
              placeholder={!s.warmup && last?.sets[i - warmCount]?.weight != null ? kg(last.sets[i - warmCount].weight) : '—'}
              onChange={(e) => setRow(i, { weight: e.target.value })} aria-label={`${labels[i]} ciężar`}
              className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl h-11 text-center text-base font-bold" />
            <Input inputMode="numeric" value={s.reps}
              placeholder={String((s.warmup ? warmups[i]?.reps : ex.reps || last?.sets[i - warmCount]?.reps) || '—')}
              onChange={(e) => setRow(i, { reps: e.target.value })} aria-label={`${labels[i]} powtórzenia`}
              className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl h-11 text-center text-base font-bold" />
            <button type="button" onClick={() => onRest(i)} aria-label={`Przerwa po: ${labels[i]}`}
              className="glass-soft h-11 rounded-2xl flex items-center justify-center text-[#C9A84C]" data-testid="start-rest">
              <Timer className="w-5 h-5" />
            </button>
          </React.Fragment>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange({ ...entry, sets: [...entry.sets, { weight: entry.sets.at(-1)?.weight ?? '', reps: '' }] })}
          className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Seria</button>
        {entry.sets.length - warmCount > 1 && (
          <button type="button" onClick={() => onChange({ ...entry, sets: entry.sets.slice(0, -1) })}
            className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5"><Minus className="w-3.5 h-3.5" /> Usuń</button>
        )}
      </div>
      <Textarea value={entry.notes} onChange={(e) => onChange({ ...entry, notes: e.target.value })} rows={2}
        placeholder="Twoja uwaga do ćwiczenia (np. ból, technika, za lekko)"
        className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl text-sm" />
    </section>
  );
};

// Quick training journal (no timer): the client writes down what they did,
// set by set. Meant for clients training on their own (online coaching).
export const WorkoutJournalPage = () => {
  const { planId, day } = useParams();
  const dayNumber = parseInt(day, 10) || 1;
  const navigate = useNavigate();
  const draftKey = `hs-journal:${planId}:${dayNumber}`;
  const [plan, setPlan] = useState(null);
  const [library, setLibrary] = useState({});
  const [last, setLast] = useState({});
  const [entries, setEntries] = useState(null);
  const [date, setDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [overall, setOverall] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restTimer, setRestTimer] = useState(null);

  // Rest after a set: the plan's rest for this exercise (library default or 60 s otherwise).
  const restFor = (ex) => ex.rest_seconds || library[ex.exercise_id]?.rest_seconds || 60;
  const startRest = (exIndex, setIndex) => {
    const ex = exercises[exIndex];
    const rows = entries[exIndex].sets;
    const labels = rowLabels(rows);
    const sec = rows[setIndex]?.warmup ? Math.min(WARMUP_REST_SECONDS, restFor(ex)) : restFor(ex);
    const nextEx = exercises[exIndex + 1];
    const next = setIndex + 1 < rows.length ? `${labels[setIndex + 1].toLowerCase()} · ${ex.exercise_name}`
      : nextEx ? `następne ćwiczenie: ${nextEx.exercise_name}` : 'koniec treningu';
    setRestTimer({ endsAt: Date.now() + sec * 1000, total: sec * 1000, label: `${ex.exercise_name} · po: ${labels[setIndex].toLowerCase()}`, next });
  };
  const extendRest = (sec) => setRestTimer((t) => {
    if (!t) return t;
    const endsAt = Math.max(t.endsAt, Date.now()) + sec * 1000;
    return { ...t, endsAt, total: Math.max(t.total, endsAt - Date.now()) };
  });

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, exRes, lastRes] = await Promise.all([
          axios.get(`${API_URL}/api/client/my-plans`, opts),
          axios.get(`${API_URL}/api/exercises`, opts),
          axios.get(`${API_URL}/api/client/last-results?plan_id=${planId}&day=${dayNumber}`, opts).catch(() => ({ data: {} })),
        ]);
        setLibrary(Object.fromEntries(exRes.data.map((e) => [e.id, e])));
        setLast(lastRes.data);
        setPlan(plansRes.data.find((p) => p.id === planId) || false);
      } catch (e) {
        console.error('Journal load failed:', e);
        setPlan(false);
      }
    })();
  }, [planId, dayNumber]);

  const exercises = useMemo(() => (plan ? plan.exercises.filter((e) => e.day_number === dayNumber)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)) : []), [plan, dayNumber]);

  // Start from the saved draft, else one row per planned set with last time's weights.
  useEffect(() => {
    if (!plan || entries) return;
    const draft = loadSaved(draftKey);
    if (draft?.entries?.length === exercises.length) {
      setEntries(draft.entries); setOverall(draft.overall || ''); if (draft.date) setDate(draft.date);
      return;
    }
    setEntries(exercises.map((ex) => ({
      notes: '',
      sets: [
        ...warmupsOf(ex).map((w) => ({ weight: kg(w.weight ?? ''), reps: '', warmup: true })),
        ...Array.from({ length: Math.max(1, ex.sets || 1) }, (_, i) => ({
          weight: kg(last[ex.exercise_id]?.sets[i]?.weight ?? ex.weight_kg ?? ''), reps: '' })),
      ],
    })));
  }, [plan, exercises, last, entries, draftKey]);

  useEffect(() => { if (entries) saveState(draftKey, { entries, overall, date }); }, [entries, overall, date, draftKey]);

  // A set counts once reps are written; prefilled weights alone don't make it done.
  const isDone = (s) => String(s.reps || '').trim() !== '';
  const filledSets = entries ? entries.reduce((n, e) => n + e.sets.filter(isDone).length, 0) : 0;

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => { const n = parseFloat(String(v).replace(',', '.')); return Number.isFinite(n) ? n : null; };
      await axios.post(`${API_URL}/api/workout-logs`, {
        plan_id: planId, day_number: dayNumber, log_date: date, source: 'journal',
        overall_notes: overall.trim() || null,
        exercises: exercises.map((ex, i) => {
          const sets = entries[i].sets.filter(isDone).map((s) => ({ reps: String(s.reps || '').trim() || null, weight: num(s.weight), done: true, warmup: !!s.warmup }));
          return { exercise_id: ex.exercise_id, exercise_name: ex.exercise_name, completed: sets.length > 0, sets, notes: entries[i].notes.trim() || null };
        }),
      }, opts);
      clearSaved(draftKey);
      toast.success('Trening zapisany');
      navigate('/client-dashboard');
    } catch (e) {
      console.error('Journal save failed:', e);
      toast.error(e.response?.data?.detail || 'Nie udało się zapisać treningu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ClientShell>
      <main className="max-w-3xl px-4 sm:px-8 py-6 sm:py-10 flex flex-col gap-5" data-testid="workout-journal">
        {plan === null && <p className="text-[var(--text-muted)]">Ładowanie…</p>}
        {plan === false && <p className="text-[var(--text-muted)]">Nie znaleziono planu.</p>}
        {plan && entries && (
          <>
            <header className="flex flex-col gap-2">
              <p className="label-text text-[#C9A84C]">Dziennik treningu · dzień {dayNumber}</p>
              <h1 className="big-num text-3xl sm:text-5xl">{plan.plan_name}</h1>
              <label className="flex items-center gap-3 text-sm text-[var(--text-muted)]">
                Data treningu
                <input type="date" value={date} max={new Date().toLocaleDateString('sv-SE')} onChange={(e) => setDate(e.target.value)}
                  className="glass-soft rounded-full px-3 h-9 text-[var(--text-primary)]" />
              </label>
            </header>
            {exercises.map((ex, i) => (
              <ExerciseCard key={`${ex.exercise_id}-${i}`} ex={ex} entry={entries[i]} last={last[ex.exercise_id]}
                onChange={(v) => setEntries((all) => all.map((e, j) => (j === i ? v : e)))}
                onInfo={library[ex.exercise_id] ? () => setPreview(library[ex.exercise_id]) : null}
                restSeconds={restFor(ex)} onRest={(setIndex) => startRest(i, setIndex)} />
            ))}
            <section className="glass rounded-[26px] p-5 sm:p-6 flex flex-col gap-3">
              <p className="label-text text-[var(--text-muted)] flex items-center gap-2"><MessageSquareQuote className="w-4 h-4" /> Jak poszło?</p>
              <Textarea value={overall} onChange={(e) => setOverall(e.target.value)} rows={3}
                placeholder="Samopoczucie, co szło dobrze, co sprawiało trudność…"
                className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl text-sm" />
            </section>
            <button type="button" onClick={save} disabled={saving || filledSets === 0}
              className="cta-primary w-full h-14 rounded-full text-[13px] font-bold tracking-[0.2em] uppercase disabled:opacity-50 mb-24 sm:mb-6"
              data-testid="journal-save">
              {saving ? 'Zapisywanie…' : filledSets ? `Zapisz trening (${filledSets} ${filledSets === 1 ? 'seria' : filledSets < 5 ? 'serie' : 'serii'})` : 'Wpisz powtórzenia w przynajmniej jednej serii'}
            </button>
          </>
        )}
      </main>
      <RestTimer timer={restTimer} onExtend={extendRest} onClose={() => setRestTimer(null)} />
      <ExerciseDetailDialog exercise={preview} open={!!preview} onOpenChange={(o) => !o && setPreview(null)} />
    </ClientShell>
  );
};
